import React, { Fragment, useState } from 'react';
import { Grid, Tab } from '@material-ui/core';
import { TabPanel, TabList, TabContext } from '@material-ui/lab';

import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import { Check, Cancel } from '@material-ui/icons';

import {
  JwlTable,
  ErrorComponent,
  LoadingSpinner,
  SectionCard,
  DetailsList,
  Chip
} from '../../../../components';

import { StyledDivider } from './styles';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';

import { userLanguage } from '../../../../core/AuthContext';
import { Exception } from '@jewelutions/core-common-domain';
import { GQLPieceReferenceHelper } from '../../../../operations/helpers/GQLPieceHelper';
import { GQLAccessoryReferenceHelper } from '../../../../operations/helpers/GQLAccessoryHelper';
import PriceLabel from '../../../../components/PriceLabel';
import { GQLParty } from '@jewelutions/core-crm-server-api-client-domain';

interface IProps {
  party: GQLParty;
}
export const PlatformSaleCatalogDetails: React.FC<IProps> = ({ party }: IProps) => {
  const [errorComponentState, setErrorComponentState] = useState(false);
  const { t: translate } = useTranslation();
  const [tabValue, setTabValue] = useState('EUR');

  const handleTabChange = (event: any, newValue: string) => {
    setTabValue(newValue);
  };

  const {
    data: saleCatalogsData,
    loading: saleCatalogsLoading,
    error: saleCatalogsError,
    refetch: refetchSaleCatalogs
  } = GQLOperationBuilder.merchant.getPlatformSaleCatalogWithPlatformMerchantContracts({
    merchantPartyId: party.id,
    includeOnlyActiveContracts: true
  });

  const {
    data: requestsData,
    loading: requestsLoading,
    error: requestsError,
    refetch: refetchRequests
  } = GQLOperationBuilder.request.getPlatformMerchantContractChangeRequestByMerchant(
    {
      merchantPartyId: party.id,
      completed: false
    },
    { fetchPolicy: 'network-only' }
  );

  const [
    approvePlatformMerchantContractChangeRequest,
    {}
  ] = GQLOperationBuilder.request.approvePlatformMerchantContractChangeRequest();

  const reloadData = async () => {
    await refetchSaleCatalogs();
    await refetchRequests();
  };

  if (saleCatalogsLoading || requestsLoading) return <LoadingSpinner />;

  if (saleCatalogsError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={saleCatalogsError?.stack}
        callback={refetchSaleCatalogs}
        message={saleCatalogsError.message}
      />
    );

  const currentSaleCatalogWithContract = GQLOperationBuilder.merchant
    .platformSaleCatalogWithPlatformMerchantContractsArrayFromResponse(saleCatalogsData)
    .find(catalog => catalog.platformSaleCatalogPublished === true);
  if (currentSaleCatalogWithContract === undefined || currentSaleCatalogWithContract === null)
    throw new Exception(`Cannot load platform sale catalog because there is no active contract.`);
  const requests = GQLOperationBuilder.request.requestArrayFromResponse(requestsData);

  const requestsRows = () => {
    return requests.map(request => {
      return {
        id: request.id,
        cells: [
          new Date(Date.parse(request.createdAt)).toLocaleString(),
          new Date(Date.parse(request.updatedAt)).toLocaleString(),
          new Date(Date.parse(request.validUntil)).toLocaleString(),
          <Button
            variant="contained"
            color={'primary'}
            onClick={async () => {
              approvePlatformMerchantContractChangeRequest({
                variables: {
                  accepted: true,
                  approverPartyId: party.id,
                  requestId: request.id,
                  note: 'Approved by platform'
                }
              }).then(_res => {
                reloadData();
              });
            }}
          >
            <Check />
            Approve
          </Button>,
          <Button
            variant="outlined"
            color={'primary'}
            onClick={async () => {
              approvePlatformMerchantContractChangeRequest({
                variables: {
                  accepted: false,
                  approverPartyId: party.id,
                  requestId: request.id,
                  note: 'Rejected by platform'
                }
              }).then(_res => {
                reloadData();
              });
            }}
          >
            <Cancel />
            <span>Reject</span>
          </Button>
        ]
      };
    });
  };

  return (
    <Fragment>
      {requests.length > 0 && (
        <JwlTable
          title="Open requests for merchant contract change"
          description={`The platform pending requests for a platform <-> merchant contract change. Until a contract change request is accepted the existing contract still applies.`}
          columns={['Request created at', 'Request updated at', 'Request valid until', 'Approve', 'Reject']}
          rows={requestsRows()}
        />
      )}

      <Grid item style={{ marginTop: '8px' }}>
        <SectionCard title="Overview" fitContent>
          <DetailsList
            list={[
              {
                primary: 'Name',
                secondary: currentSaleCatalogWithContract.platformSaleCatalog.name[userLanguage]!
              },
              {
                primary: 'Description',
                secondary: currentSaleCatalogWithContract.platformSaleCatalog.description[userLanguage]!
              },
              {
                primary: 'Published',
                secondary: (
                  <Chip
                    size="small"
                    mr={1}
                    mb={1}
                    label={currentSaleCatalogWithContract.platformSaleCatalog.isPublished ? 'YES' : 'NO'}
                    $active={currentSaleCatalogWithContract.platformMerchantContractActive}
                  />
                )
              },
              {
                primary: 'Updated At',
                secondary: new Date(
                  Date.parse(currentSaleCatalogWithContract.platformSaleCatalog.updatedAt)
                ).toLocaleString()
              }
            ]}
          />
        </SectionCard>
      </Grid>
      {currentSaleCatalogWithContract.platformSaleCatalog.platformSellingPricingCatalogs.length > 0 && (
        <Grid item style={{ marginTop: '8px' }}>
          <SectionCard title="Selling prices">
            <TabContext value={tabValue}>
              <TabList
                onChange={(event, tabIndex) => handleTabChange(event, tabIndex)}
                aria-label="currency-tabs"
              >
                {currentSaleCatalogWithContract.platformSaleCatalog.platformSellingPricingCatalogs.map(
                  platformSellingPricingCatalog => {
                    return (
                      <Tab
                        label={`${platformSellingPricingCatalog.currency} Pricing Catalog`}
                        value={platformSellingPricingCatalog.currency}
                      />
                    );
                  }
                )}
              </TabList>
              {currentSaleCatalogWithContract.platformSaleCatalog.platformSellingPricingCatalogs.map(
                platformSellingPricingCatalog => {
                  const piecePricingColumns = [
                    'Size Type',
                    'Material Type',
                    'Plating Type',
                    'Stones Placement Type',
                    'Platform Price',
                    'Merchant Retail Reference Price',
                    'Merchant Retail Margin'
                  ];

                  const accessoriesPricingColumns = [
                    'Size Type',
                    'Material Type',
                    'Plating Type',
                    'Platform Price',
                    'Merchant Retail Reference Price',
                    'Merchant Retail Margin'
                  ];

                  const merchantReferenceRetailPricingCatalog = currentSaleCatalogWithContract.platformSaleCatalog.merchantReferenceRetailPricingCatalogs.find(
                    c => c.currency === platformSellingPricingCatalog.currency
                  );
                  if (
                    merchantReferenceRetailPricingCatalog === undefined ||
                    merchantReferenceRetailPricingCatalog === null
                  )
                    throw new Exception(
                      `Cannot locate merchant retail reference pricing catalog for currency ${platformSellingPricingCatalog.currency}`
                    );

                  const getPiecePricingRows = () => {
                    return platformSellingPricingCatalog.pieces.map(platformSellingPiecePrice => {
                      const merchantPieceReferenceRetailPrice = merchantReferenceRetailPricingCatalog.pieces.find(
                        p =>
                          GQLPieceReferenceHelper.identical(
                            platformSellingPiecePrice.pieceReference,
                            p.pieceReference
                          )
                      );
                      if (merchantPieceReferenceRetailPrice === undefined)
                        throw new Exception(
                          `Cannot match merchant reference retail price for piece reference ${JSON.stringify(
                            platformSellingPiecePrice.pieceReference
                          )}`
                        );

                      return {
                        id: platformSellingPiecePrice.pieceReference.pieceId,
                        cells: [
                          platformSellingPiecePrice.pieceReference.pieceConfiguration.sizeType,
                          platformSellingPiecePrice.pieceReference.pieceConfiguration.materialType,
                          platformSellingPiecePrice.pieceReference.pieceConfiguration.platingType,
                          platformSellingPiecePrice.pieceReference.pieceConfiguration.stonesPlacementType,
                          <PriceLabel price={platformSellingPiecePrice.price} />,
                          <PriceLabel price={merchantPieceReferenceRetailPrice.price} />,
                          parseFloat(
                            merchantPieceReferenceRetailPrice.price.amount /
                              platformSellingPiecePrice.price.amount +
                              ''
                          ).toFixed(2)
                        ]
                      };
                    });
                  };

                  const getAccessoryPricingRows = () => {
                    return platformSellingPricingCatalog.accessories.map(platformSellingAccessoryPrice => {
                      const merchantAccessoryReferenceRetailPrice = merchantReferenceRetailPricingCatalog.accessories.find(
                        a =>
                          GQLAccessoryReferenceHelper.identical(
                            platformSellingAccessoryPrice.accessoryReference,
                            a.accessoryReference
                          )
                      );
                      if (merchantAccessoryReferenceRetailPrice === undefined)
                        throw new Exception(
                          `Cannot match merchant reference retail price for accessory reference ${JSON.stringify(
                            platformSellingAccessoryPrice.accessoryReference
                          )}`
                        );

                      return {
                        id: platformSellingAccessoryPrice.accessoryReference.accessoryId,
                        cells: [
                          platformSellingAccessoryPrice.accessoryReference.accessoryConfiguration.sizeType,
                          platformSellingAccessoryPrice.accessoryReference.accessoryConfiguration
                            .materialType,
                          platformSellingAccessoryPrice.accessoryReference.accessoryConfiguration.platingType,
                          <PriceLabel price={platformSellingAccessoryPrice.price} />,
                          <PriceLabel price={merchantAccessoryReferenceRetailPrice.price} />,
                          parseFloat(
                            merchantAccessoryReferenceRetailPrice.price.amount /
                              platformSellingAccessoryPrice.price.amount +
                              ''
                          ).toFixed(2)
                        ]
                      };
                    });
                  };

                  return (
                    <TabPanel value={platformSellingPricingCatalog.currency}>
                      <JwlTable
                        title="Pieces Pricing Catalog"
                        description="If you have been reading the overrides documentation page but you are, add more metadata..."
                        columns={piecePricingColumns}
                        rows={getPiecePricingRows()}
                        height="400px"
                        size="small"
                      />
                      <StyledDivider />
                      <JwlTable
                        title="Accessories Pricing Catalog"
                        description="If you have been reading the overrides documentation page but you are, add more metadata..."
                        columns={accessoriesPricingColumns}
                        rows={getAccessoryPricingRows()}
                        height="400px"
                        size="small"
                      />
                    </TabPanel>
                  );
                }
              )}
            </TabContext>
          </SectionCard>
        </Grid>
      )}
    </Fragment>
  );
};
